import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TeamIntroduction = () => {
  const [activeTeamMember, setActiveTeamMember] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });

  const teamMembers = [
    {
      id: 1,
      name: "Sudhakar Pandey",
      role: "Backend Developer",
      expertise: "Java, Spring Boot, Node.js",
      bio: "Backend Developer skilled in Java, Spring Boot, and Node.js, focused on building scalable and secure server-side applications. Experienced in designing RESTful APIs, microservices, and database-driven systems with clean, efficient, and maintainable code.",
      image: "/sudhakar.jpeg",
      skills: ["Java", "Spring boot", "AWS", "Node js"],
      achievement: "Led 10+ successful project deliveries",
      color: "from-blue-500/20 to-cyan-500/20"
    },
    {
      id: 2,
      name: "Noor Uddin Sheikh",
      role: "Frontend Developer",
      expertise: "React js, Three.js, Motion Design",
      bio: "Frontend Developer with expertise in React.js, Next.js, and Three.js, specializing in crafting interactive, high-performance, and visually engaging web applications. Passionate about delivering seamless user experiences through modern design, optimized performance, and clean, scalable code.",
      image: "/noor.jpeg",
      skills: ["Three.js", "React js", "Next js", "GSAP"],
      achievement: "Created 23+ 3D web experiences",
      color: "from-purple-500/20 to-pink-500/20"
    },
    {
      id: 3,
      name: "Aviral Saxena",
      role: "QA / Tester",
      expertise: "Selenium, QA, Java automation",
      bio: "Software Tester & QA Engineer with expertise in manual testing and automation using Selenium. Skilled in test case design, bug tracking, and regression testing, ensuring high-quality and reliable software delivery. Experienced in functional, UI, and cross-browser testing, with a strong focus on accuracy, efficiency, and continuous improvement.",
      image: "/aviral.jpg",
      skills: ["Selenium", "Automation", "QA"],
      achievement: "Improved user engagement by 300%",
      color: "from-green-500/20 to-emerald-500/20"
    },
    {
      id: 4,
      name: "Shrishtika Rai",
      role: "Frontend Developer",
      expertise: "React js, Redux toolkit, Next js",
      bio: "Frontend Developer with expertise in React.js and Next.js, focused on building responsive, high-performance, and user-friendly web applications. Passionate about creating seamless user experiences with modern UI design and efficient code architecture.",
      image: "/shristika.png",
      skills: ["Next js", "React js", "Redux"],
      achievement: "Delivered 28+ successful projects",
      color: "from-orange-500/20 to-red-500/20"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const memberVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-b from-muted/20 to-background">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Meet Our <span className="text-accent">Creative Team</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Talented individuals who bring together technical expertise and creative vision to deliver exceptional digital experiences.
          </p>
        </motion.div>

        {/* Team Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {teamMembers?.map((member, index) => (
            <motion.div
              key={member?.id}
              variants={memberVariants}
              className="group cursor-pointer"
              onClick={() => setActiveTeamMember(index)}
            >
              <div className={`relative bg-gradient-to-br ${member?.color} backdrop-blur-sm border border-border/50 rounded-2xl p-6 transform-3d transition-all duration-500 ${activeTeamMember === index ? 'scale-105 shadow-accent' : 'hover:scale-102'
                }`}>
                {/* Member Image */}
                <div className="relative mb-4">
                  <div className="w-20 h-20 mx-auto rounded-2xl overflow-hidden">
                    <Image
                      src={member?.image}
                      alt={member?.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>

                  {/* Active Indicator */}
                  {activeTeamMember === index && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-accent rounded-full flex items-center justify-center"
                    >
                      <Icon name="Check" size={14} color="var(--color-primary)" />
                    </motion.div>
                  )}
                </div>

                {/* Member Info */}
                <div className="text-center">
                  <h3 className="text-lg font-bold text-foreground mb-1 group-hover:text-accent transition-colors">
                    {member?.name}
                  </h3>
                  <p className="text-sm text-accent font-medium mb-2">
                    {member?.role}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {member?.expertise}
                  </p>
                </div>

                {/* Hover Overlay */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: activeTeamMember === index ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 bg-gradient-to-br from-accent/5 to-accent/10 backdrop-blur-[1px] rounded-2xl pointer-events-none"
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Active Member Details */}
        <motion.div
          key={activeTeamMember}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className={`bg-gradient-to-br ${teamMembers?.[activeTeamMember]?.color} backdrop-blur-sm border border-border/50 rounded-2xl p-8`}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              {/* Large Profile Image */}
              <div className="lg:col-span-1">
                <div className="relative">
                  <div className="w-48 h-48 mx-auto rounded-3xl overflow-hidden shadow-3d">
                    <Image
                      src={teamMembers?.[activeTeamMember]?.image}
                      alt={teamMembers?.[activeTeamMember]?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Floating Achievement Badge */}
                  <motion.div
                    initial={{ scale: 0, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="absolute -bottom-4 -right-4 bg-accent text-accent-foreground px-4 py-2 rounded-xl text-sm font-medium shadow-accent"
                  >
                    {teamMembers?.[activeTeamMember]?.achievement}
                  </motion.div>
                </div>
              </div>

              {/* Member Details */}
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h3 className="text-3xl font-bold text-foreground mb-2">
                    {teamMembers?.[activeTeamMember]?.name}
                  </h3>
                  <p className="text-xl text-accent font-medium mb-4">
                    {teamMembers?.[activeTeamMember]?.role}
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    {teamMembers?.[activeTeamMember]?.bio}
                  </p>
                </div>

                {/* Skills */}
                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-3">Core Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {teamMembers?.[activeTeamMember]?.skills?.map((skill, skillIndex) => (
                      <motion.span
                        key={skillIndex}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 * skillIndex }}
                        className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-medium border border-accent/20"
                      >
                        {skill}
                      </motion.span>
                    ))}
                  </div>
                </div>

                {/* Contact Button */}
                {/* <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center space-x-2 bg-accent text-accent-foreground px-6 py-3 rounded-xl font-medium shadow-accent hover:bg-accent/90 transition-all duration-300"
                >
                  <Icon name="MessageCircle" size={18} />
                  <span>Connect with {teamMembers?.[activeTeamMember]?.name?.split(' ')?.[0]}</span>
                </motion.button> */}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TeamIntroduction;